/**
 * Application Constants
 * Centralized configuration values that were previously hardcoded
 *
 * Usage:
 * import { APP_CONFIG, CACHE_CONFIG, BREAKPOINTS } from './lib/constants';
 */

// ==================== APP CONFIGURATION ====================
export const APP_CONFIG = {
  // App Info
  name: "Jamaica Driver's Guide",
  shortName: "JM Driver Guide",
  version: "2.1.0",

  // URLs
  baseUrl: process.env.REACT_APP_BASE_URL || 'https://jamaicadriversguide.com',

  // Contact/Support
  supportEmail: 'info@jamaicadriversguide.com',

  // Phone format placeholder (Jamaica)
  phoneFormat: '876-XXX-XXXX',
  phonePattern: /^876-\d{3}-\d{4}$/,

  // Default country code
  countryCode: '+1-876',
};

// ==================== CACHE CONFIGURATION ====================
export const CACHE_CONFIG = {
  // Weather cache TTL (10 minutes)
  weatherTTL: 10 * 60 * 1000,

  // Session storage TTL
  sessionTTL: 24 * 60 * 60 * 1000, // 24 hours

  // Default fetch timeout
  defaultTimeout: 5000,

  // Profile fetch timeout
  profileFetchTimeout: 5000,

  // Service provider load timeout
  serviceProviderTimeout: 10000,
};

// ==================== HOTSPOT EXPIRATION ====================
export const EXPIRATION_HOURS = {
  checkpoint: 6,      // Police checkpoints expire in 6 hours
  speed_trap: 4,      // Speed traps expire in 4 hours
  accident: 2,        // Accidents expire in 2 hours
  hazard: 8,          // Hazards expire in 8 hours
  traffic: 2,         // Traffic jams expire in 2 hours
  other: 4,           // Default expiration
};

// ==================== BREAKPOINTS ====================
export const BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
  desktop: 1280,
};

// ==================== LISTING TIERS ====================
export const DEFAULT_LISTING_TIERS = {
  basic: {
    name: 'basic',
    displayName: 'Basic',
    price: 3500,
    currency: 'JMD',
    features: ['Business name & contact', 'Parish listing', 'Basic visibility'],
  },
  premium: {
    name: 'premium',
    displayName: 'Premium',
    price: 5000,
    currency: 'JMD',
    features: ['All Basic features', 'Priority placement', 'Logo display', 'Extended description'],
  },
};

// ==================== VALIDATION THRESHOLDS ====================
export const VALIDATION = {
  // Minimum confirmations for verified status
  minConfirmations: 3,

  // Maximum flags before auto-deactivation
  maxFlags: 3,

  // Reputation score thresholds
  reputation: {
    trusted: 100,
    regular: 50,
    active: 20,
  },
};

// ==================== API LIMITS ====================
export const API_LIMITS = {
  // Maximum results per page
  pageSize: 20,

  // Maximum search results
  maxSearchResults: 100,

  // Maximum file upload size (5MB)
  maxFileSize: 5 * 1024 * 1024,

  // Maximum toast notifications
  maxToasts: 5,
};

// ==================== JAMAICA PARISHES ====================
export const PARISHES = [
  'Kingston',
  'St. Andrew',
  'St. Thomas',
  'Portland',
  'St. Mary',
  'St. Ann',
  'Trelawny',
  'St. James',
  'Hanover',
  'Westmoreland',
  'St. Elizabeth',
  'Manchester',
  'Clarendon',
  'St. Catherine',
];

// ==================== THEME COLORS ====================
export const THEME = {
  primary: {
    50: '#ecfdf5',
    100: '#d1fae5',
    500: '#10b981',
    600: '#059669',
    700: '#047857',
    800: '#065f46',
    900: '#064e3b',
  },
  accent: {
    400: '#facc15',
    500: '#eab308',
    600: '#ca8a04',
  },
};

// ==================== ANALYTICS ====================
export const ANALYTICS = {
  // GA4 placeholder - to be replaced with actual ID
  ga4MeasurementIdPlaceholder: 'G-XXXXXXXXXX',

  // Event names
  events: {
    pageView: 'page_view',
    offenceView: 'view_item',
    directoryView: 'view_item',
    directoryClick: 'select_content',
    adImpression: 'ad_impression',
    adClick: 'ad_click',
    search: 'search',
  },
};

export default {
  APP_CONFIG,
  CACHE_CONFIG,
  EXPIRATION_HOURS,
  BREAKPOINTS,
  DEFAULT_LISTING_TIERS,
  VALIDATION,
  API_LIMITS,
  PARISHES,
  THEME,
  ANALYTICS,
};
